import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Chip, CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { toast } from 'react-toastify';
import { smartCardAPI, BACKEND_BASE_URL } from '../services/api';

export default function SmartCards() {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    smartCardAPI.list()
      .then((res) => setCards(res.data.results || res.data))
      .catch(() => toast.error('Failed to load smart cards'))
      .finally(() => setLoading(false));
  }, []);

  const handleDownload = (card, format) => {
    if (card.assets_status === 'pending' || card.assets_status === 'processing') {
      toast.info('Assets are still being generated. Please try again shortly.');
      return;
    }
    setDownloadingId(card.id);
    smartCardAPI.download(card.id, format)
      .then((res) => {
        if (res.status === 202) {
          toast.info('Assets are still being generated. Please try again shortly.');
          return;
        }
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${card.card_id}_smartcard.${format}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(() => toast.error('Download failed'))
      .finally(() => setDownloadingId(null));
  };

  const statusColor = (s) => {
    if (s === 'ready') return 'success';
    if (s === 'failed') return 'error';
    if (s === 'processing') return 'warning';
    return 'default';
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">Smart Cards</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/admin/smart-cards/generate')}>
          Generate New Card
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Card ID</TableCell>
                <TableCell>Gemstone</TableCell>
                <TableCell>Weight</TableCell>
                <TableCell>Quality Grade</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Issued</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cards.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((card) => (
                <TableRow key={card.id}>
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{card.card_id}</TableCell>
                  <TableCell>{card.gemstone_name}</TableCell>
                  <TableCell>{card.weight || '—'}</TableCell>
                  <TableCell>
                    <Chip label={card.quality_grade || 'N/A'} size="small" color={card.quality_grade ? 'primary' : 'default'} />
                  </TableCell>
                  <TableCell>
                    <Chip label={card.assets_status || 'ready'} size="small" color={statusColor(card.assets_status)} />
                  </TableCell>
                  <TableCell>{new Date(card.issue_date).toLocaleDateString()}</TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      {card.front_image && card.assets_status === 'ready' && (
                        <Button size="small" startIcon={<VisibilityIcon />}
                          onClick={() => window.open(card.front_image.startsWith('http') ? card.front_image : `${BACKEND_BASE_URL}${card.front_image}`, '_blank')}>
                          Front
                        </Button>
                      )}
                      {card.back_image && card.assets_status === 'ready' && (
                        <Button size="small" startIcon={<VisibilityIcon />}
                          onClick={() => window.open(card.back_image.startsWith('http') ? card.back_image : `${BACKEND_BASE_URL}${card.back_image}`, '_blank')}>
                          Back
                        </Button>
                      )}
                      <Button size="small"
                        disabled={downloadingId === card.id || card.assets_status !== 'ready'}
                        startIcon={downloadingId === card.id ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                        onClick={() => handleDownload(card, 'pdf')}>
                        {downloadingId === card.id ? 'Downloading...' : 'PDF'}
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div" count={cards.length} page={page}
          onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </Paper>
    </Box>
  );
}
